import { useAlert } from '@/common/alert/AlertContext';
import { TitleButton } from '@/common/buttons/TitleButton';
import { KrDialog } from '@/common/dialog/Dialog';
import { useDialog } from '@/common/dialog/DialogContext';
import { EntitySelectDialog } from '@/common/dialog/EntitySelectDialog';
import { Grid } from '@/common/grid/Grid';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDateInt, formatPercent, mapHeaders, normalizeCell, parseTsv } from './bulkGridUtils';

const PasteTextDialog = ({ closeDialog }) => {
  const [text, setText] = useState('');
  return (
    <KrDialog
      title="Paste rows"
      subtitle="Paste tab-separated rows copied from a spreadsheet, including the header row."
      maxWidth="md"
      closeDialog={closeDialog}
      buttons={[
        <Button key="cancel" variant="outlined" color="secondary" onClick={() => closeDialog()}>
          Cancel
        </Button>,
        <Button key="apply" variant="contained" onClick={() => closeDialog(text)} disabled={!text.trim()}>
          Apply
        </Button>,
      ]}
    >
      <TextField
        multiline
        minRows={8}
        maxRows={20}
        fullWidth
        autoFocus
        placeholder="prod_code	value	start_date…"
        value={text}
        onChange={(event) => setText(event.target.value)}
      />
    </KrDialog>
  );
};

const StatusCellRenderer = ({ value }) => {
  const colorByStatus = { Error: 'error', Warning: 'warning', Update: 'info', New: 'success' };
  return <Chip size="small" variant="outlined" color={colorByStatus[value] ?? 'default'} label={value} />;
};

// Shows the code (and resolved name) with a magnifier that opens the standard EntitySelectDialog
// Mirrors EntitySelect: search opens the standard EntitySelectDialog; once a value is set the icon clears it
const EntityCellRenderer = (props) => {
  const { openDialog } = useDialog();
  const column = props.colDef.bulkColumn;
  const code = props.value;
  const node = column.codeToNode?.[code];

  // mousedown (not click) so the grid's single-click edit never swallows the action
  const onIconMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (code) {
      props.node.setDataValue(column.field, null);
      return;
    }
    openDialog(EntitySelectDialog, {
      data: {
        module: column.module,
        entity: column.entity,
        expr: column.expr,
        query: column.query,
        field: column.field,
      },
    }).then((selected) => {
      if (!selected || selected.length === 0) return;
      props.node.setDataValue(column.field, selected[0].code);
    });
  };

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ height: '100%' }}>
      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {code}
        {node?.name ? ` (${node.name})` : ''}
      </Box>
      {props.colDef.editable && (
        <FontAwesomeIcon
          icon={code ? fas.faTimes : fas.faSearch}
          style={{ cursor: 'pointer', marginLeft: 8, flexShrink: 0 }}
          color="gray"
          onMouseDown={onIconMouseDown}
        />
      )}
    </Stack>
  );
};

/**
 * Paste-from-spreadsheet bulk editor for one Entity.
 *
 * columns: [{ field, header, displayName?, type: text|number|percent|date|boolean|enum|entity, required?, fixed?,
 *             typeForRow?(row), typeDependsOn?, values? (enum: [{code, displayName}]),
 *             module/entity/expr/query? (entity), width? }]
 *   - header is the exact spreadsheet column name users must paste (matches the Excel upload templates)
 *   - fixed columns are supplied by the caller (fixedValues) and not shown
 *
 * preloadRows: existing rows to start from (edit mode); each may carry any column field plus id
 * classifyRow(row): 'NEW' | 'UPDATE' for the status badge
 * validateRow(row): { [field]: message } of extra client-side errors
 * warnRow(row): { [field]: message } of non-blocking warnings
 * onSubmit(rows): Promise<{ ok, errors: [{ rowIdx, field, message }], results }>; rows carry rowIdx and fixedValues
 * exportRows: rows to offer via "Copy rows" (same column layout) to close the Excel round trip
 */
export const EntityBulkGridDialog = ({
  closeDialog,
  title,
  subtitle,
  columns,
  defaultsFields = [],
  preloadRows = [],
  fixedValues = {},
  classifyRow,
  validateRow,
  warnRow,
  onSubmit,
  exportRows,
  header,
}) => {
  const rsrc = useRsrc();
  const theme = useTheme();
  const { openDialog } = useDialog();
  const { showAlert } = useAlert();

  const gridRef = useRef();
  const rowsRef = useRef([]);
  const nextIdxRef = useRef(0);

  const [showOnlyIssues, setShowOnlyIssues] = useState(false);
  const [counts, setCounts] = useState({ total: 0, errors: 0, warnings: 0 });
  const [saving, setSaving] = useState(false);
  const [lookupsReady, setLookupsReady] = useState(false);

  // Grid callbacks are registered once; they read the latest state through these refs
  const showOnlyIssuesRef = useRef(false);
  const validateAllRef = useRef(() => {});
  showOnlyIssuesRef.current = showOnlyIssues;

  const visibleColumns = useMemo(() => columns.filter((column) => !column.fixed), [columns]);

  // Entity columns resolve codes client-side so unknown codes light up before hitting the server
  useEffect(() => {
    const entityColumns = columns.filter((column) => column.type === 'entity');
    Promise.all(
      entityColumns.map((column) =>
        rsrc.rows(column.module, column.entity, { expr: column.expr, query: column.query }).then((rows) => {
          column.codeToNode = Object.fromEntries((rows ?? []).map((row) => [row.code, row]));
        }),
      ),
    ).then(() => setLookupsReady(true));
  }, []);

  const getGridApi = () => gridRef.current?.gridApiRef?.current;

  const effectiveValue = (row, field) => {
    const value = row[field];
    if (value != null && value !== '') return value;
  };

  const effectiveRow = (row) => {
    const result = { ...fixedValues };
    visibleColumns.forEach((column) => {
      result[column.field] = effectiveValue(row, column.field);
    });
    result.rowIdx = row._rowIdx;
    return result;
  };

  const validateAll = useCallback(() => {
    let errors = 0;
    let warnings = 0;
    rowsRef.current.forEach((row) => {
      const rowErrors = { ...(row._parse ?? {}) };
      const rowWarnings = { ...(row._parseWarnings ?? {}) };
      const eff = effectiveRow(row);

      visibleColumns.forEach((column) => {
        const value = eff[column.field];
        if (column.required && (value == null || value === '') && !rowErrors[column.field]) {
          rowErrors[column.field] = 'Required';
        }
        if (column.type === 'entity' && value && column.codeToNode && !column.codeToNode[value]) {
          rowErrors[column.field] = `${column.displayName ?? column.header} not found: ${value}`;
        }
      });

      Object.assign(rowErrors, validateRow?.(eff) ?? {});
      Object.assign(rowWarnings, warnRow?.(eff) ?? {});
      Object.assign(rowErrors, row._server ?? {});

      row._errors = rowErrors;
      row._warnings = rowWarnings;
      row._action = classifyRow ? classifyRow(eff) : 'NEW';
      if (Object.keys(rowErrors).length > 0) errors++;
      else if (Object.keys(rowWarnings).length > 0) warnings++;
    });
    setCounts({ total: rowsRef.current.length, errors, warnings });
    const api = getGridApi();
    if (api) {
      api.setGridOption('rowData', [...rowsRef.current]);
      api.refreshCells({ force: true });
      api.onFilterChanged();
    }
  }, [visibleColumns, classifyRow, validateRow, warnRow, lookupsReady]);
  validateAllRef.current = validateAll;

  useEffect(() => {
    validateAll();
  }, [validateAll]);

  const newRow = (values = {}) => {
    const row = { _rowIdx: nextIdxRef.current++, _parse: {}, _parseWarnings: {} };
    // Checkbox cells need a real boolean, never undefined
    visibleColumns.forEach((column) => {
      if (column.type === 'boolean') row[column.field] = false;
    });
    return { ...row, ...values };
  };

  // Normalizes raw pasted/preloaded cell values into the row, recording parse problems per field
  const assignRaw = (row, field, raw) => {
    const column = columns.find((c) => c.field === field);
    if (!column) return;
    const effectiveColumn = column.typeForRow ? { ...column, type: column.typeForRow(row) } : column;
    const { value, error, warning } = normalizeCell(effectiveColumn, raw);
    row[field] = value;
    if (error) row._parse[field] = error;
    else delete row._parse[field];
    if (warning) row._parseWarnings[field] = warning;
    else delete row._parseWarnings[field];
    delete row._server?.[field];
  };

  const addRows = (rows) => {
    rowsRef.current = [...rowsRef.current, ...rows];
    validateAllRef.current();
  };

  useEffect(() => {
    if (preloadRows.length === 0) return;
    const rows = preloadRows.map((preload) => {
      const row = newRow({ id: preload.id });
      visibleColumns.forEach((column) => assignRaw(row, column.field, preload[column.field]));
      return row;
    });
    addRows(rows);
  }, []);

  const applyPastedText = (text) => {
    const cells = parseTsv(text);
    if (cells.length === 0) return;
    const { fields, error } = mapHeaders(cells[0], columns);
    if (error) {
      showAlert(error, 'warning');
      return;
    }
    const rows = cells
      .slice(1)
      .filter((line) => line.some((cell) => cell !== ''))
      .map((line) => {
        const row = newRow();
        line.forEach((cell, index) => {
          if (fields[index]) assignRaw(row, fields[index], cell);
        });
        return row;
      });
    if (rows.length === 0) {
      showAlert('No data rows found under the header row.', 'warning');
      return;
    }
    addRows(rows);
  };

  // Ctrl+V anywhere in the dialog (except inside an input) is treated as a spreadsheet paste
  useEffect(() => {
    const onPaste = (event) => {
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;
      const text = event.clipboardData?.getData('text/plain');
      if (!text) return;
      event.preventDefault();
      applyPastedText(text);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [visibleColumns]);

  const copyToClipboard = (lines, what) => {
    navigator.clipboard.writeText(lines.join('\n')).then(() => showAlert(`${what} copied to clipboard`, 'success'));
  };

  const headerLine = () => visibleColumns.map((column) => column.header).join('\t');

  const copyHeaders = () => copyToClipboard([headerLine()], 'Headers');

  const copyRows = () => {
    const lines = [headerLine()];
    (exportRows ?? []).forEach((row) => {
      lines.push(
        visibleColumns
          .map((column) => {
            const value = row[column.field];
            if (value == null) return '';
            if (column.type === 'date') return formatDateInt(value);
            if (column.type === 'boolean') return value ? 'Y' : 'N';
            return String(value);
          })
          .join('\t'),
      );
    });
    copyToClipboard(lines, `${exportRows?.length ?? 0} rows`);
  };

  const deleteSelected = () => {
    const api = getGridApi();
    const selected = new Set((api?.getSelectedRows() ?? []).map((row) => row._rowIdx));
    if (selected.size === 0) return;
    rowsRef.current = rowsRef.current.filter((row) => !selected.has(row._rowIdx));
    validateAllRef.current();
  };

  const submit = async () => {
    validateAll();
    if (rowsRef.current.length === 0) return;
    if (rowsRef.current.some((row) => Object.keys(row._errors).length > 0)) {
      setShowOnlyIssues(true);
      showAlert('Fix the highlighted cells before saving. Nothing has been saved.', 'warning');
      return;
    }
    setSaving(true);
    try {
      const payload = rowsRef.current.map((row) => ({ ...effectiveRow(row), id: row.id }));
      const result = await onSubmit(payload);
      if (result?.ok) {
        closeDialog(result);
        return;
      }
      const byIdx = Object.fromEntries(rowsRef.current.map((row) => [row._rowIdx, row]));
      (result?.errors ?? []).forEach(({ rowIdx, field, message }) => {
        const row = byIdx[rowIdx];
        if (!row) return;
        row._server ??= {};
        row._server[field ?? '_row'] = message;
      });
      setShowOnlyIssues(true);
      validateAll();
      showAlert('The server rejected the batch. Nothing has been saved; see the highlighted rows.', 'warning');
    } finally {
      setSaving(false);
    }
  };

  const errorStyle = { backgroundColor: theme.palette.mode === 'dark' ? '#5a2323' : '#ffcccc' };
  const warningStyle = { backgroundColor: theme.palette.mode === 'dark' ? '#5a4a1e' : '#fff3c4' };

  const fieldDefs = useMemo(() => {
    const label = (field) => {
      if (field === '_row') return 'Row';
      const column = columns.find((c) => c.field === field);
      return column?.displayName ?? column?.header ?? field;
    };

    const statusCol = {
      field: '_status',
      displayName: 'Status',
      width: 110,
      pinned: 'left',
      editable: false,
      sortable: false,
      filter: false,
      valueGetter: (params) => {
        const row = params.data;
        if (!row) return '';
        if (Object.keys(row._errors ?? {}).length > 0) return 'Error';
        if (Object.keys(row._warnings ?? {}).length > 0) return 'Warning';
        return row._action === 'UPDATE' ? 'Update' : 'New';
      },
      cellRenderer: StatusCellRenderer,
      tooltipValueGetter: (params) => {
        const row = params.data;
        if (!row) return null;
        const messages = [
          ...Object.entries(row._errors ?? {}).map(([field, message]) => `${label(field)}: ${message}`),
          ...Object.entries(row._warnings ?? {}).map(([field, message]) => `${label(field)}: ${message}`),
        ];
        return messages.length > 0 ? messages.join('\n') : null;
      },
    };

    const cols = visibleColumns.map((column) => {
      const colDef = {
        field: column.field,
        displayName: column.displayName ?? column.header,
        headerTooltip: `Spreadsheet column: ${column.header}`,
        width: column.width ?? 140,
        editable: true,
        filter: false,
        bulkColumn: column,
        cellStyle: (params) => {
          if (params.data?._errors?.[column.field]) return errorStyle;
          if (params.data?._warnings?.[column.field]) return warningStyle;
          return null;
        },
        tooltipValueGetter: (params) =>
          params.data?._errors?.[column.field] ?? params.data?._warnings?.[column.field] ?? null,
        valueParser: (params) => {
          assignRaw(params.data, column.field, params.newValue);
          return params.data[column.field];
        },
      };

      if (column.type === 'percent' || column.typeForRow) {
        colDef.valueFormatter = (params) =>
          (column.typeForRow?.(params.data) ?? column.type) === 'percent' ? formatPercent(params.value) : params.value;
      }
      if (column.type === 'date') colDef.valueFormatter = (params) => formatDateInt(params.value);
      if (column.type === 'boolean') {
        // The renderer toggles the value itself; no popup editor
        colDef.cellRenderer = 'agCheckboxCellRenderer';
        colDef.cellEditor = undefined;
        colDef.singleClickEdit = false;
        colDef.suppressKeyboardEvent = () => true;
        colDef.valueParser = undefined;
        colDef.valueGetter = (params) => params.data?.[column.field] === true;
        colDef.valueSetter = (params) => {
          params.data[column.field] = params.newValue === true;
          return true;
        };
      }
      if (column.type === 'enum') {
        colDef.cellEditor = 'agSelectCellEditor';
        colDef.cellEditorParams = { values: (column.values ?? []).map((option) => option.code) };
        colDef.valueParser = undefined;
      }
      if (column.type === 'entity') {
        colDef.cellRenderer = EntityCellRenderer;
        colDef.width = column.width ?? 240;
      }
      if (column.cellRenderer) {
        colDef.cellRenderer = column.cellRenderer;
      }
      return colDef;
    });

    return [statusCol, ...cols];
  }, [visibleColumns, theme.palette.mode]);

  const gridOptions = useMemo(
    () => ({
      fieldDefs,
      rowModelType: 'clientSide',
      rowSelection: 'multiple',
      getRowId: (params) => String(params.data._rowIdx),
      suppressClipboardPaste: true,
      tooltipShowDelay: 300,
      singleClickEdit: true,
      stopEditingWhenCellsLoseFocus: true,
      isExternalFilterPresent: () => showOnlyIssuesRef.current,
      doesExternalFilterPass: (node) =>
        Object.keys(node.data?._errors ?? {}).length > 0 || Object.keys(node.data?._warnings ?? {}).length > 0,
      onCellValueChanged: (event) => {
        // valueParser already normalized; checkbox/select editors bypass it
        const column = event.colDef.bulkColumn;
        if (column && (column.type === 'boolean' || column.type === 'enum')) {
          assignRaw(event.data, column.field, event.newValue);
        }
        visibleColumns
          .filter((candidate) => candidate.typeForRow && candidate.typeDependsOn?.includes(column?.field))
          .forEach((candidate) => assignRaw(event.data, candidate.field, event.data[candidate.field]));
        delete event.data._server?.[column?.field];
        validateAllRef.current();
      },
      getContextMenuItems: (params) => {
        if (!params.node) return [];
        return [
          { name: 'Delete selected rows', icon: fas.faTrash, action: deleteSelected },
          { name: 'Add row', icon: fas.faPlus, action: () => addRows([newRow()]) },
        ];
      },
    }),
    [fieldDefs],
  );

  useEffect(() => {
    getGridApi()?.onFilterChanged();
  }, [showOnlyIssues]);

  const titleButtons = (
    <>
      <TitleButton icon={fas.faPlus} toolTip="Add row" onClick={() => addRows([newRow()])} />
      <TitleButton
        icon={fas.faClipboard}
        toolTip="Paste rows from text"
        onClick={() => openDialog(PasteTextDialog, {}).then((text) => text && applyPastedText(text))}
      />
      <TitleButton icon={fas.faListCheck} toolTip="Copy headers to clipboard" onClick={copyHeaders} />
      {exportRows && <TitleButton icon={fas.faCopy} toolTip="Copy current rows to clipboard" onClick={copyRows} />}
    </>
  );

  const buttons = [
    <Button key="cancel" variant="outlined" color="secondary" onClick={() => closeDialog()} disabled={saving}>
      Cancel
    </Button>,
    <Button
      key="save"
      variant="contained"
      onClick={submit}
      disabled={saving || counts.total === 0 || counts.errors > 0}
      data-test-id="save-button"
    >
      Save all
    </Button>,
  ];

  const ready = counts.total - counts.errors - counts.warnings;

  return (
    <KrDialog
      title={title}
      subtitle={subtitle}
      titleButtons={titleButtons}
      maxWidth="xl"
      closeDialog={() => closeDialog()}
      buttons={buttons}
    >
      {header}

      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body2">
          {counts.total} row{counts.total === 1 ? '' : 's'}
          {counts.total > 0 && ` · ${ready} ready`}
          {counts.errors > 0 && ` · ${counts.errors} need attention`}
          {counts.warnings > 0 && ` · ${counts.warnings} warning${counts.warnings === 1 ? '' : 's'}`}
        </Typography>
        <FormControlLabel
          sx={{ ml: 'auto' }}
          control={
            <Switch size="small" checked={showOnlyIssues} onChange={(e) => setShowOnlyIssues(e.target.checked)} />
          }
          label="Show only issues"
        />
      </Stack>

      <Box sx={{ position: 'relative', height: '60vh', minHeight: 420, display: 'flex' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Grid
            ref={gridRef}
            gridOptions={gridOptions}
            events={{
              onGridReady: () => {
                getGridApi()?.setGridOption('rowData', [...rowsRef.current]);
              },
            }}
          />
        </Box>
        {counts.total === 0 && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              top: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Stack spacing={1} alignItems="center" sx={{ pointerEvents: 'auto' }}>
              <Typography color="text.secondary">Paste rows here (Ctrl+V) with a header row, or add a row.</Typography>
              <Tooltip title={visibleColumns.map((column) => column.header).join(' · ')}>
                <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                  Expected columns: {visibleColumns.map((column) => column.header).join(' · ')}
                </Typography>
              </Tooltip>
            </Stack>
          </Box>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary">
        Nothing is saved until every row is valid; the whole batch is saved together.
      </Typography>
    </KrDialog>
  );
};
